import type { AutonomousCursor, CursorPage, CueTarget } from '../autonomous-cursor.js';
import type { BeforeActionContext, BeforeActionHook, BeforeActionOutcome } from '../click-timing.js';

/**
 * Binds the anticipatory cursor cue to the `run_code` page proxy's before-action
 * hook: render the cue, wait long enough for it to survive frame throttling, then
 * let the real Playwright call through.
 *
 * **Abort is checked on every path, including the skipped-cue path.**
 * `executeRunCode` races the snippet against its signal but cannot cancel it, so a
 * snippet that outlives its deadline can still dispatch a real action afterwards.
 * This hook is the first place that can refuse — and it refuses whether or not a
 * cue actually rendered, so offscreen and failed-box actions get the same
 * protection as cued ones.
 *
 * **Dwell is credited, never silently spent.** When a budget is supplied (cache
 * replay), each dwell is credited to that action's deadline up to a hard cap, so
 * visualization can never consume the snippet's correctness budget. The outer
 * run-wide cancellation signal is deliberately NOT extended.
 *
 * @module
 */

/** Receives dwell so the owning deadline can be extended, bounded by its own cap. */
export interface CueBudget {
  creditMs(ms: number): void;
}

/** Displayed forms for keys whose raw Playwright name reads poorly on a keycap. */
const KEY_GLYPHS: Record<string, string> = {
  Enter: '⏎ Enter',
  NumpadEnter: '⏎ Enter',
  Escape: 'Esc',
  Tab: '⇥ Tab',
  Backspace: '⌫',
  Delete: '⌦',
  ArrowUp: '↑',
  ArrowDown: '↓',
  ArrowLeft: '←',
  ArrowRight: '→',
  Control: 'Ctrl',
  Meta: '⌘',
  Alt: '⌥',
  Shift: '⇧',
  Space: 'Space',
  ' ': 'Space',
  PageUp: 'PgUp',
  PageDown: 'PgDn',
};

/** Cap shown for free-text entry — never the typed value itself. */
const TEXT_CAP = 'abc';

/**
 * Split a Playwright key string into displayable keycap labels.
 *
 * `'Control+A'` → `['Ctrl', 'A']`, `'Enter'` → `['⏎ Enter']`, `'ArrowDown'` → `['↓']`.
 * A bare `'+'` (a real, if rare, key) survives because an empty split result falls
 * back to the original string.
 */
export function normalizeKeyLabels(key: string): string[] {
  const parts = key
    .split('+')
    .filter((p) => p.length > 0)
    .map((p) => KEY_GLYPHS[p] ?? p);
  return parts.length > 0 ? parts : [key];
}

/** Extract `{ position }` from a click/hover option bag, if present. */
function readPosition(args: readonly unknown[]): { x: number; y: number } | undefined {
  const opts = args.find((a) => a && typeof a === 'object' && !Array.isArray(a)) as
    | Record<string, unknown>
    | undefined;
  const pos = opts?.position as { x?: unknown; y?: unknown } | undefined;
  if (!pos || typeof pos.x !== 'number' || typeof pos.y !== 'number') return undefined;
  return { x: pos.x, y: pos.y };
}

/**
 * Keycap labels for an element-targeted method.
 *
 * `press` shows the actual key. Free-text methods show a neutral `abc` cap and
 * never the typed content — the same doctrine the type glow follows (it reads only
 * the bounding box, never `.value`).
 */
function elementKeyLabels(method: string, args: readonly unknown[]): string[] | undefined {
  if (method === 'press' && typeof args[0] === 'string') return normalizeKeyLabels(args[0]);
  if (method === 'fill' || method === 'type' || method === 'pressSequentially') return [TEXT_CAP];
  return undefined;
}

/** Keycap labels for a `page.keyboard.*` call. */
function keyboardLabels(method: string, args: readonly unknown[]): string[] {
  if ((method === 'press' || method === 'down') && typeof args[0] === 'string') {
    return normalizeKeyLabels(args[0]);
  }
  return [TEXT_CAP];
}

/** Sleep that resolves early when the signal aborts — never leaves a dangling timer. */
function sleepOrAbort(ms: number, signal: AbortSignal): Promise<void> {
  if (ms <= 0 || signal.aborted) return Promise.resolve();
  return new Promise<void>((resolve) => {
    const timer = setTimeout(finish, ms);
    function finish() {
      clearTimeout(timer);
      signal.removeEventListener('abort', finish);
      resolve();
    }
    signal.addEventListener('abort', finish, { once: true });
  });
}

/**
 * Build the before-action hook for one `run_code` block.
 *
 * Returns `aborted` — refusing dispatch — at three checkpoints: before resolving
 * the cue, after it renders, and immediately before the underlying call.
 */
export function createRunCodeCueHook(opts: {
  page: CursorPage;
  cursor: AutonomousCursor;
  signal: AbortSignal;
  budget?: CueBudget;
}): BeforeActionHook {
  const { page, cursor, signal, budget } = opts;

  return async (ctx: BeforeActionContext): Promise<BeforeActionOutcome> => {
    // Checkpoint 1 — before any cue work, and on the skipped-cue path too.
    if (signal.aborted) return 'aborted';

    const dwell = await renderCue(page, cursor, ctx);

    // A skipped cue still has to re-arm the document's cue state, or an action
    // whose box was unresolvable (or a legacy selector action that is never cued)
    // would ALSO lose the reactive ripple for the rest of the block after a
    // navigation — the new document has no active latch, and across an origin
    // change no sessionStorage flag either.
    if (dwell === 0) await cursor.syncState(page);

    // Checkpoint 2 — the cue may have taken a round trip; the deadline may have
    // passed while it did.
    if (signal.aborted) return 'aborted';

    if (dwell > 0) {
      budget?.creditMs(dwell);
      await sleepOrAbort(dwell, signal);
      // Checkpoint 3 — immediately before dispatch.
      if (signal.aborted) return 'aborted';
    }
    return 'proceed';
  };
}

/** Dispatch to the right cue for this target kind. Returns the dwell to wait. */
async function renderCue(
  page: CursorPage,
  cursor: AutonomousCursor,
  ctx: BeforeActionContext
): Promise<number> {
  switch (ctx.kind) {
    case 'locator':
    case 'element-handle': {
      if (!ctx.element) return 0;
      return cursor.cueElement(page, ctx.element as CueTarget, {
        position: readPosition(ctx.args),
        keys: elementKeyLabels(ctx.method, ctx.args),
      });
    }
    case 'mouse': {
      const [x, y] = ctx.args;
      if (typeof x !== 'number' || typeof y !== 'number') return 0;
      return cursor.cuePoint(page, x, y);
    }
    case 'keyboard':
      return cursor.cueKeys(page, keyboardLabels(ctx.method, ctx.args));
    default:
      // Page/Frame legacy selector APIs and FrameLocator factories are never cued,
      // but they still reach checkpoint 1/2 above for abort purposes.
      return 0;
  }
}
